import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteHeader from '../components/layout/SiteHeader';
import PageBreadcrumb from '../components/layout/PageBreadcrumb';
import SiteLayout, { SiteMain } from '../components/layout/SiteLayout';
import SupportSubNav from '../components/layout/SupportSubNav';
import { useSupportWidget } from '../contexts/SupportWidgetContext';
import {
  SUPPORT_EMAIL,
  SUPPORT_INBOX_EMAIL,
  SUPPORT_PHONE,
  getWhatsAppUrl
} from '../constants/siteNav';
import BrandLogo from '../components/brand/BrandLogo';
import { BRAND_NAME } from '../constants/brand';

const ContactUsPage = () => {
  const { openEmailWidget } = useSupportWidget();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('channel') === 'email') {
      openEmailWidget();
    }
  }, [searchParams, openEmailWidget]);

  return (
    <SiteLayout className="bg-white">
      <SiteHeader />
      <PageBreadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Contact' }]} />
      <SupportSubNav />

      <SiteMain>
      {/* Hero - GoDaddy-style mint panel */}
      <section className="relative bg-[#E8F4F3] overflow-hidden">
        <div className="container mx-auto px-4 py-12 md:py-16 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 items-center max-w-6xl mx-auto">
            <div>
              <div className="flex items-center gap-2.5 mb-5">
                <BrandLogo link={false} size="sm" imgClassName="rounded-md" />
                <span className="text-sm font-semibold text-gray-700">{BRAND_NAME} Support</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-[2.75rem] font-bold text-gray-900 leading-tight tracking-tight mb-4">
                We&apos;re here to help 24/7
              </h1>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-lg">
                Whether you&apos;re buying your first ticket, listing an event, or need help with
                M-Pesa - our support team is ready to assist you every step of the way.
              </p>
            </div>

            <div className="hidden lg:flex justify-end">
              <div className="relative w-72 h-72 xl:w-80 xl:h-80">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-200/40 to-teal-200/30 blur-2xl" />
                <div className="relative w-full h-full rounded-2xl bg-gradient-to-br from-orange-100 to-teal-50 border border-white/60 shadow-xl flex items-center justify-center">
                  <div className="text-center px-6">
                    <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center mx-auto mb-4">
                      <i className="fas fa-headset text-3xl text-orange-500" />
                    </div>
                    <p className="text-sm font-semibold text-gray-800">Expert support team</p>
                    <p className="text-xs text-gray-500 mt-1">Tickets · Events · Payments</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Action card - overlaps hero */}
      <section className="relative z-10 px-4 -mt-8 md:-mt-10 pb-16">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-white rounded-xl shadow-[0_8px_40px_rgba(0,0,0,0.08)] border border-gray-100 p-8 md:p-10">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2">Get in touch</h2>
            <p className="text-sm text-gray-600 mb-8">
              Choose how you&apos;d like to reach us - email for detailed enquiries, WhatsApp for quick help.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={openEmailWidget}
                className="flex-1 inline-flex items-center justify-center gap-2.5 bg-gray-900 hover:bg-gray-800 text-white px-6 py-4 rounded-lg font-semibold text-sm transition-colors shadow-md"
              >
                <i className="fas fa-envelope text-base" />
                Email us
              </button>
              <a
                href={getWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 inline-flex items-center justify-center gap-2.5 bg-white hover:bg-gray-50 text-gray-900 px-6 py-4 rounded-lg font-semibold text-sm border-2 border-gray-200 hover:border-gray-300 transition-colors"
              >
                <i className="fab fa-whatsapp text-[#25D366] text-xl" />
                WhatsApp
              </a>
            </div>

            <p className="text-center text-xs text-gray-400 mt-5 font-medium tracking-wide uppercase">
              Support is available 24/7 on WhatsApp. Email replies usually within minutes to an hour.
            </p>
          </div>
        </div>
      </section>

      {/* Contact details */}
      <section className="bg-gray-50 border-t border-gray-100 py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center md:text-left">
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mx-auto md:mx-0 mb-4 shadow-sm">
                <i className="fas fa-envelope text-gray-700" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">Support email</h3>
              <a href={`mailto:${SUPPORT_INBOX_EMAIL}`} className="text-teal-700 hover:text-teal-800 font-medium text-sm">
                {SUPPORT_INBOX_EMAIL}
              </a>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                For tickets, refunds, account access, and organizer enquiries.
              </p>
            </div>

            <div className="text-center md:text-left">
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mx-auto md:mx-0 mb-4 shadow-sm">
                <i className="fab fa-whatsapp text-[#25D366] text-lg" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">WhatsApp</h3>
              <a href={getWhatsAppUrl()} target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:text-teal-800 font-medium text-sm">
                {SUPPORT_PHONE}
              </a>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Fastest for urgent questions - available 24/7.
              </p>
            </div>

            <div className="text-center md:text-left">
              <div className="w-12 h-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center mx-auto md:mx-0 mb-4 shadow-sm">
                <i className="fas fa-info-circle text-gray-700" />
              </div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-2">General enquiries</h3>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="text-teal-700 hover:text-teal-800 font-medium text-sm">
                {SUPPORT_EMAIL}
              </a>
              <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                Partnerships, press, and general information.
              </p>
            </div>
          </div>
        </div>
      </section>
      </SiteMain>
    </SiteLayout>
  );
};

export default ContactUsPage;
